import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useProgramTheme from '../../../common/use-program-theme';

import { Button } from '@socialchorus/shared-ui-components';
import { useUserGeneratedContent } from '../../../models/user-generated-contents/context';
import { useUserPermissions } from '../../../common/hooks/use-user-permissions';
import { usePatronSelector } from '../../../common/use-patron-selector';
import { advocateSelectors } from '../../../models/advocate';
import CoverImage from './sidebar-cards/cover-image/cover-image';
import SettingsDropdown from './settings-dropdown';
import TopicPickerCard from './sidebar-cards/topic-picker-card';
import DeliverSection from './sidebar-cards/deliver-section';
import NotificationsCard from './sidebar-cards/notifications-card';
import './sidebar.scss';
import cx from 'classnames';

type UGCSidebarProps = {
  onSave: () => void;
  onSubmit: () => void;
};

const UserGeneratedContentSidebar = ({ onSave, onSubmit }: UGCSidebarProps) => {
  const { t } = useTranslation();
  const { isPersisting, isLoading, validateForSave, validateForSubmit } =
    useUserGeneratedContent();
  // Publish-targeting UI (Audience picker, Notification Channels) gates on
  // publishAccess (Litmus content:edit) per FE-31683 — granular loading
  // so we don't wait on the studio-access query.
  const { publishAccess, isPublishLoading } = useUserPermissions();
  const { themeColor } = useProgramTheme();

  const [isSaving, setIsSaving] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Delay the loading class names by 300ms so brief saves/submits don't flash
  // the loading treatment; remove them immediately when the flag clears.
  const [showSaveLoadingClass, setShowSaveLoadingClass] = useState(false);
  const [showPublishLoadingClass, setShowPublishLoadingClass] = useState(false);

  // FE-31698: Publish + Save Draft share one inline-error flow across member
  // and Studio users. Buttons stay enabled (modulo isPersisting); validation
  // runs on click and surfaces per-field inline errors. AC for Save Draft:
  // "just a title is needed."
  const handleSubmit = () => {
    if (isPersisting) return;
    if (!validateForSubmit()) return;
    setIsSubmitting(true);
    onSubmit();
  };

  const handleSave = () => {
    if (isPersisting) return;
    if (!validateForSave()) return;
    setIsSaving(true);
    onSave();
  };

  useEffect(() => {
    if (!isPersisting) {
      setIsSaving(false);
      setShowSaveLoadingClass(false);
      setIsSubmitting(false);
      setShowPublishLoadingClass(false);
      return undefined;
    }

    const timer = setTimeout(() => {
      if (isSaving) {
        setShowSaveLoadingClass(true);
      } else if (isSubmitting) {
        setShowPublishLoadingClass(true);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [isPersisting, isSaving, isSubmitting]);

  // FE-31698 + FE-31683: the Deliver section's Audience picker and the
  // Notification Channels card are both publish-targeting UI, so both gate on
  // publishAccess (Litmus content:edit) — not on studioAccess. Use the
  // granular isPublishLoading flag so we don't hide the audience picker while
  // the studio-access query is still in flight.
  const showPublishControls =
    !isLoading && !isPublishLoading && !!publishAccess;

  // FE-31685: Save Draft moved out of the settings dropdown onto the sidebar
  // action row. Hide the gear entirely when no per-program toggles remain to
  // render — otherwise the trigger opens an empty popover.
  const translationsEnabled = usePatronSelector(
    advocateSelectors.getCanTranslateSubmission
  );
  const commentingEnabled = usePatronSelector(
    advocateSelectors.getCanCommentSubmission
  );
  const sharingEnabled = usePatronSelector(
    advocateSelectors.getCanShareSubmission
  );
  const showSettingsDropdown =
    translationsEnabled || commentingEnabled || sharingEnabled;

  return (
    <div>
      <div className="ugc_sidebar__actions">
        {showSettingsDropdown && <SettingsDropdown disabled={isPersisting} />}
        <Button
          variant="outline"
          className={cx('ugc_sidebar__save_draft_button', {
            'ugc_sidebar__save_draft_button-loading': showSaveLoadingClass,
          })}
          disabled={isPersisting}
          isLoading={isSaving}
          onClick={handleSave}
          label={t('user_generated_content.save_as_draft')}
          aria-label={t('user_generated_content.save_as_draft')}
        />
        <Button
          style={{ backgroundColor: themeColor }}
          variant="primary"
          className={cx('ugc_sidebar__publish_button', {
            'ugc_sidebar__publish_button-loading': showPublishLoadingClass,
          })}
          disabled={isPersisting}
          isLoading={isSubmitting}
          onClick={handleSubmit}
          label={t('user_generated_content.publish_post')}
          aria-label={t('user_generated_content.publish_post')}
        />
      </div>
      {!isLoading &&
        (showPublishControls ? (
          <DeliverSection showAudiencePicker />
        ) : (
          <TopicPickerCard />
        ))}
      {showPublishControls && <NotificationsCard />}
      <CoverImage />
    </div>
  );
};

export default UserGeneratedContentSidebar;
